import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Star, Lock, PenLine } from "lucide-react";
import { Link } from "react-router-dom";
import { HitAvatar } from "@/components/avatar/HitAvatar";

/**
 * Shows the current user's OWN reputation card:
 *  - visible average and total (reciprocated ratings only)
 *  - list of reviews received (double-blind unlocked)
 *  - how many ratings are still locked behind the counter-party (nudge)
 *  - how many transactions the user still owes a rating on (own action)
 *
 * Double-blind is preserved at the transaction level — a rating only appears
 * here once BOTH parties of that order rated.
 */
export default function MyReputationCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/reputation")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data) return null;

  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-6" data-testid="my-reputation-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl">Mi reputación</h2>
          <p className="text-xs text-neutral-500 mt-1">Solo aparecen las notas donde ambos ya calificaron (doble ciego por transacción).</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {data.avg !== null ? (
            <div className="flex items-center gap-1 bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2" data-testid="my-reputation-avg">
              <Star size={16} className="fill-[#EF4444] text-[#EF4444]" />
              <b className="text-lg text-white">{data.avg}</b>
              <span className="text-xs text-neutral-500">({data.count} reseña{data.count === 1 ? "" : "s"})</span>
            </div>
          ) : (
            <div className="text-sm text-neutral-500 bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2">
              Aún sin calificaciones visibles
            </div>
          )}
        </div>
      </div>

      {(data.hidden_pending > 0 || data.pending_to_give > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-testid="my-reputation-nudges">
          {data.hidden_pending > 0 && (
            <div className="bg-[#0A0A0A] border border-amber-500/30 rounded p-3 flex items-start gap-2" data-testid="my-reputation-hidden">
              <Lock size={16} className="text-amber-400 mt-0.5 shrink-0" />
              <div className="text-xs text-neutral-300 leading-snug">
                Tienes <b className="text-white">{data.hidden_pending}</b> nota{data.hidden_pending === 1 ? "" : "s"} recibida{data.hidden_pending === 1 ? "" : "s"} que aún <b>no aparece{data.hidden_pending === 1 ? "" : "n"}</b> — se destrabará{data.hidden_pending === 1 ? "" : "n"} cuando esa contraparte también califique.
              </div>
            </div>
          )}
          {data.pending_to_give > 0 && (
            <div className="bg-[#0A0A0A] border border-[#EF4444]/40 rounded p-3 flex items-start gap-2" data-testid="my-reputation-pending-give">
              <PenLine size={16} className="text-[#EF4444] mt-0.5 shrink-0" />
              <div className="text-xs text-neutral-300 leading-snug">
                Te faltan <b className="text-white">{data.pending_to_give}</b> calificación{data.pending_to_give === 1 ? "" : "es"} por dar. Al calificar destrabas la nota que te dieron y sube tu reputación visible.{" "}
                <Link to="/" className="text-[#EF4444] hover:underline">Ir a calificar</Link>
              </div>
            </div>
          )}
        </div>
      )}

      {data.reviews && data.reviews.length > 0 ? (
        <div className="space-y-2" data-testid="my-reputation-reviews">
          {data.reviews.map((r) => (
            <div key={r.id || r.order_id} className="bg-[#0A0A0A] border border-[#262626] rounded p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex" aria-label={`${r.stars} estrellas`}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={14} className={n <= r.stars ? "fill-[#EF4444] text-[#EF4444]" : "text-neutral-700"} />
                  ))}
                </div>
                <HitAvatar avatarId={r.from_avatar_id} name={r.from_name} size={32} lite />
                <span className="text-xs text-neutral-400">de <b className="text-white">{r.from_name}</b></span>
                <span className="text-xs text-neutral-600 ml-auto">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString("es-EC", { day: "2-digit", month: "short", year: "2-digit" }) : ""}
                </span>
              </div>
              {r.comment && <p className="text-sm text-neutral-300 mt-2 leading-snug">{r.comment}</p>}
            </div>
          ))}
        </div>
      ) : (
        data.hidden_pending === 0 && data.pending_to_give === 0 && (
          <div className="text-sm text-neutral-500 text-center py-4">Todavía no tienes reseñas destrabadas. Cierra tus primeras ventas/compras y anima a la otra parte a calificar.</div>
        )
      )}
    </div>
  );
}
